import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Spinner,
  Text,
  makeStyles,
  tokens,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHeaderCell,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import type { EducationUser, EducationClass, EducationSchool, EmailAuthMethod } from '../../types';
import educationService from '../../services/educationService';
import { EmptyState } from '../Common/EmptyState';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '950px',
    width: '90vw',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px',
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    padding: '16px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  sectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorBrandForeground1,
    marginBottom: '8px',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
  },
  fieldLabel: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    marginBottom: '4px',
  },
  fieldValue: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    wordBreak: 'break-word',
  },
  table: {
    backgroundColor: tokens.colorNeutralBackground1,
    marginTop: '8px',
  },
  emptyMessage: {
    padding: '16px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  twoColumnLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  columnCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  columnHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  columnIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
});

interface UserDetailsProps {
  userId: string | null;
  open: boolean;
  onClose: () => void;
}

export const UserDetails: React.FC<UserDetailsProps> = ({
  userId,
  open,
  onClose,
}) => {
  const styles = useStyles();
  const [user, setUser] = useState<EducationUser | null>(null);
  const [classes, setClasses] = useState<EducationClass[]>([]);
  const [ownedClasses, setOwnedClasses] = useState<EducationClass[]>([]);
  const [schools, setSchools] = useState<EducationSchool[]>([]);
  const [emailAuthMethod, setEmailAuthMethod] = useState<EmailAuthMethod | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingOwnedClasses, setLoadingOwnedClasses] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && userId) {
      loadUserDetails();
      loadUserClasses();
      loadUserOwnedClasses();
      loadUserSchools();
      loadEmailAuthMethod();
    }
  }, [open, userId]);

  const loadUserDetails = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await educationService.getUserById(userId);
      setUser(response);
    } catch (err: any) {
      console.error('Error loading user details:', err);
      setError(err.message || 'Erro ao carregar detalhes do usuário');
    } finally {
      setLoading(false);
    }
  };

  const loadUserClasses = async () => {
    if (!userId) return;

    try {
      setLoadingClasses(true);
      const response = await educationService.getUserClasses(userId);
      setClasses(response);
    } catch (err: any) {
      console.error('Error loading user classes:', err);
      // Don't set main error, just log it
    } finally {
      setLoadingClasses(false);
    }
  };

  const loadUserOwnedClasses = async () => {
    if (!userId) return;

    try {
      setLoadingOwnedClasses(true);
      const response = await educationService.getUserOwnedClasses(userId);
      setOwnedClasses(response);
    } catch (err: any) {
      console.error('Error loading user owned classes:', err);
      // Don't set main error, just log it
    } finally {
      setLoadingOwnedClasses(false);
    }
  };

  const loadUserSchools = async () => {
    if (!userId) return;

    try {
      setLoadingSchools(true);
      const response = await educationService.getUserSchools(userId);
      setSchools(response);
    } catch (err: any) {
      console.error('Error loading user schools:', err);
      // Don't set main error, just log it
    } finally {
      setLoadingSchools(false);
    }
  };

  const loadEmailAuthMethod = async () => {
    if (!userId) return;

    try {
      const response = await educationService.getUserEmailAuthMethod(userId);
      setEmailAuthMethod(response);
    } catch (err: any) {
      console.error('Error loading email auth method:', err);
      // Don't set main error, just log it
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'student':
        return <Badge color="informative">Aluno</Badge>;
      case 'teacher':
        return <Badge color="success">Professor</Badge>;
      case 'faculty':
        return <Badge color="important">Coordenador</Badge>;
      default:
        return <Badge appearance="outline">Outro</Badge>;
    }
  };

  const renderField = (label: string, value: any) => {
    return (
      <div>
        <div className={styles.fieldLabel}>{label}</div>
        <div className={styles.fieldValue}>{value || '-'}</div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            }
          >
            Detalhes do Usuário
          </DialogTitle>

          <DialogContent>
            {loading && (
              <div className={styles.loading}>
                <Spinner label="Carregando detalhes..." />
              </div>
            )}

            {error && (
              <div className={styles.error}>
                <Text>Erro: {error}</Text>
              </div>
            )}

            {!loading && !error && user && (
              <div className={styles.content}>
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Informações Gerais</div>
                  <div className={styles.fieldRow}>
                    {renderField('Nome Completo', user.displayName)}
                    {renderField('Tipo de Usuário', getRoleBadge(user.primaryRole))}
                  </div>
                  <div className={styles.fieldRow}>
                    {renderField('Nome', user.givenName)}
                    {renderField('Nome do Meio', user.middleName || '-')}
                  </div>
                  <div className={styles.fieldRow}>
                    {renderField('Sobrenome', user.surname)}
                    {renderField('Apelido de Email', user.mailNickname)}
                  </div>
                  <div className={styles.fieldRow}>
                    {renderField('UPN', user.userPrincipalName)}
                    {renderField('Email', user.mail)}
                  </div>
                  <div className={styles.fieldRow}>
                    {renderField('ID Microsoft', user.id)}
                  </div>
                  <div className={styles.fieldRow}>
                    {renderField('E-mail Pessoal', emailAuthMethod?.emailAddress)}
                    {renderField('Autenticação por E-mail',
                      emailAuthMethod
                        ? (emailAuthMethod.isEnabled ? 'Ativo' : 'Não configurado')
                        : '-'
                    )}
                  </div>
                </div>

                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Informações de Conta</div>
                  <div className={styles.fieldRow}>
                    {renderField('Departamento', user.department)}
                    {renderField('ID do Funcionário', user.employeeId)}
                  </div>
                  <div className={styles.fieldRow}>
                    {renderField('Conta Habilitada', user.accountEnabled !== undefined ? (user.accountEnabled ? 'Sim' : 'Não') : '-')}
                  </div>
                </div>

                {user.student && (
                  <div className={styles.section}>
                    <div className={styles.sectionTitle}>Informações do Aluno</div>
                    <div className={styles.fieldRow}>
                      {renderField('ID Externo', user.student.externalId)}
                      {renderField('Número do Aluno (RA)', user.student.studentNumber)}
                    </div>
                    <div className={styles.fieldRow}>
                      {renderField('Série/Ano', user.student.grade)}
                      {renderField('Ano de Formatura', user.student.graduationYear)}
                    </div>
                    {user.student.birthDate && (
                      <div className={styles.fieldRow}>
                        {renderField('Data de Nascimento', new Date(user.student.birthDate).toLocaleDateString('pt-BR'))}
                      </div>
                    )}
                  </div>
                )}

                {user.teacher && (
                  <div className={styles.section}>
                    <div className={styles.sectionTitle}>Informações do Professor</div>
                    <div className={styles.fieldRow}>
                      {renderField('ID Externo', user.teacher.externalId)}
                      {renderField('Número do Professor (Matrícula)', user.teacher.teacherNumber)}
                    </div>
                  </div>
                )}

                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Informações de Sincronização</div>
                  <div className={styles.fieldRow}>
                    {renderField('Data de Criação', user.createdDateTime ? new Date(user.createdDateTime).toLocaleString('pt-BR') : '-')}
                  </div>
                </div>

                {/* Turmas como Professor */}
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Turmas como Professor ({ownedClasses.length})</div>
                  {loadingOwnedClasses ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                      <Spinner size="small" label="Carregando turmas..." />
                    </div>
                  ) : ownedClasses.length > 0 ? (
                    <Table className={styles.table} size="small">
                      <TableHeader>
                        <TableRow>
                          <TableHeaderCell style={{ width: '70%' }}>Nome da Turma</TableHeaderCell>
                          <TableHeaderCell style={{ width: '30%' }}>ID Externo</TableHeaderCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ownedClasses.map((cls) => (
                          <TableRow key={cls.id}>
                            <TableCell>
                              <Text weight="semibold">{cls.displayName}</Text>
                            </TableCell>
                            <TableCell>{cls.externalId || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <EmptyState variant="inline" message="Nenhuma turma como professor" />
                  )}
                </div>

                {/* Turmas como Aluno */}
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Turmas como Aluno ({classes.length})</div>
                  {loadingClasses ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                      <Spinner size="small" label="Carregando turmas..." />
                    </div>
                  ) : classes.length > 0 ? (
                    <Table className={styles.table} size="small">
                      <TableHeader>
                        <TableRow>
                          <TableHeaderCell style={{ width: '70%' }}>Nome da Turma</TableHeaderCell>
                          <TableHeaderCell style={{ width: '30%' }}>ID Externo</TableHeaderCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {classes.map((cls) => (
                          <TableRow key={cls.id}>
                            <TableCell>
                              <Text weight="semibold">{cls.displayName}</Text>
                            </TableCell>
                            <TableCell>{cls.externalId || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <EmptyState variant="inline" message="Nenhuma turma como aluno" />
                  )}
                </div>

                {/* Schools Section */}
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Escolas Associadas ({schools.length})</div>
                  {loadingSchools ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                      <Spinner size="small" label="Carregando escolas..." />
                    </div>
                  ) : schools.length > 0 ? (
                    <Table className={styles.table} size="small">
                      <TableHeader>
                        <TableRow>
                          <TableHeaderCell>Nome da Escola</TableHeaderCell>
                          <TableHeaderCell>Número da Escola</TableHeaderCell>
                          <TableHeaderCell>ID Externo</TableHeaderCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {schools.map((school) => (
                          <TableRow key={school.id}>
                            <TableCell>
                              <Text weight="semibold">{school.displayName}</Text>
                            </TableCell>
                            <TableCell>{school.schoolNumber || '-'}</TableCell>
                            <TableCell>{school.externalId || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <EmptyState variant="inline" message="Nenhuma escola associada" />
                  )}
                </div>
              </div>
            )}
          </DialogContent>

          <DialogActions>
            <Button appearance="primary" onClick={onClose}>
              Fechar
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default UserDetails;
